import type { PortalUserView } from "@portal/schemas";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@portal/ui";
import { Pencil } from "lucide-react";
import { useState } from "react";
import { Hint } from "../../components/Hint";
import { ROLE_LABELS } from "./roles";
import { KANA_HINT, UserNameForm } from "./UserNameDialog";

/**
 * ユーザー設定(本人。ユーザー要望 2026-10-15)。アカウントメニューの「ユーザー設定」と、名前が未登録の案内から開く。
 * 欄ごとに分け、今後ほかの設定もここに足す。名前はふだん表示だけで、「編集」を押すと入力欄になる
 * (ユーザー要望 2026-10-15)。保存すると表示に戻り「保存しました」と出す(モーダルは閉じない)。
 * startEditing: 未登録の案内(「名前を設定する」)から開いたときは、最初から編集の状態にする。
 */
export function UserSettingsDialog({
  user,
  open,
  onOpenChange,
  startEditing = false,
}: {
  user: PortalUserView;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  startEditing?: boolean;
}) {
  const [editing, setEditing] = useState(startEditing);
  const [saved, setSaved] = useState(false);
  const profile = user.profile;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>ユーザー設定</DialogTitle>
          <DialogDescription className="sr-only">
            自分のアカウントと名前の設定です。
          </DialogDescription>
        </DialogHeader>
        <section aria-labelledby="settings-account" className="flex flex-col gap-2">
          <h3 id="settings-account" className="text-sm font-semibold">
            アカウント
          </h3>
          <dl className="grid grid-cols-[6rem_1fr] gap-y-1 text-sm">
            <dt className="text-muted-foreground">メール</dt>
            <dd className="break-all">{user.email}</dd>
            <dt className="text-muted-foreground">権限</dt>
            <dd>{user.roles.map((r) => ROLE_LABELS[r] ?? r).join("、") || "なし"}</dd>
          </dl>
        </section>
        <section aria-labelledby="settings-name" className="flex flex-col gap-3 border-t pt-4">
          <div className="flex items-center gap-1.5">
            <h3 id="settings-name" className="text-sm font-semibold">
              名前
            </h3>
            <Hint label="名前の説明">{KANA_HINT}</Hint>
            {!editing && profile && (
              <Button
                variant="outline"
                size="sm"
                className="ml-auto"
                onClick={() => {
                  setSaved(false);
                  setEditing(true);
                }}
              >
                <Pencil />
                編集
              </Button>
            )}
          </div>
          {!profile ? (
            <p className="text-sm text-muted-foreground">名前の記録を読み込めませんでした。</p>
          ) : editing ? (
            <UserNameForm
              mode="self"
              target={{ email: user.email, ...profile }}
              onSaved={() => {
                setEditing(false);
                setSaved(true);
              }}
              onCancel={() => setEditing(false)}
            />
          ) : (
            <dl className="grid grid-cols-[6rem_1fr] gap-y-1 text-sm">
              <dt className="text-muted-foreground">名前</dt>
              <dd>
                {user.nameRegistered ? (
                  `${profile.familyName} ${profile.givenName}`
                ) : (
                  <span className="text-muted-foreground">未設定</span>
                )}
              </dd>
              <dt className="text-muted-foreground">よみがな</dt>
              <dd>
                {user.nameRegistered ? (
                  `${profile.familyNameKana} ${profile.givenNameKana}`
                ) : (
                  <span className="text-muted-foreground">未設定</span>
                )}
              </dd>
            </dl>
          )}
          {saved && !editing && (
            <p role="status" className="text-sm text-muted-foreground">
              保存しました
            </p>
          )}
        </section>
      </DialogContent>
    </Dialog>
  );
}
